import { useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

/**
 * Cloudflare Turnstile widget component.
 * @param {function} onVerify - callback(token) when verification succeeds
 * @param {function} onExpire - callback when token expires
 * @param {string} theme    - 'light' | 'dark' | 'auto'
 * ref exposes: { reset() }
 */
const TurnstileWidget = forwardRef(({ onVerify, onExpire, theme = 'auto' }, ref) => {
  const containerRef = useRef(null);
  const widgetId = useRef(null);
  const failedRef = useRef(false);

  const handleVerify = useCallback((token) => {
    if (onVerify) onVerify(token);
  }, [onVerify]);

  const handleExpire = useCallback(() => {
    if (onExpire) onExpire();
    if (onVerify) onVerify(null);
  }, [onExpire, onVerify]);

  // Without an error-callback, Turnstile THROWS uncaught cross-origin
  // exceptions (e.g. 110200 = domain not whitelisted on preview hosts),
  // which surface as opaque "Script error." overlays. Handle them here:
  // log, hide the widget, and let the form proceed without a token.
  const handleError = useCallback((code) => {
    console.warn(`[Turnstile] error ${code} — continuing without bot check`);
    failedRef.current = true;
    if (containerRef.current) containerRef.current.style.display = 'none';
    if (onVerify) onVerify(null);
    return true; // tell Turnstile the error was handled (no rethrow/retry)
  }, [onVerify]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetId.current !== null && window.turnstile) {
        window.turnstile.reset(widgetId.current);
      }
    }
  }));

  useEffect(() => {
    if (!SITE_KEY || !containerRef.current) return;

    const render = () => {
      if (failedRef.current) return;
      if (widgetId.current !== null && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
      if (window.turnstile && containerRef.current) {
        widgetId.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: handleVerify,
          'expired-callback': handleExpire,
          'error-callback': handleError,
          theme,
          size: 'flexible',
        });
      }
    };

    // Wait for Turnstile script to load
    if (window.turnstile) {
      render();
    } else {
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          render();
        }
      }, 200);
      return () => clearInterval(interval);
    }

    return () => {
      if (widgetId.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch (e) { console.warn('Turnstile cleanup:', e.message); }
        widgetId.current = null;
      }
    };
  }, [handleVerify, handleExpire, handleError, theme]);

  if (!SITE_KEY) return null;

  return (
    <div
      ref={containerRef}
      data-testid="turnstile-widget"
      style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}
    />
  );
});

export default TurnstileWidget;
